import { useState, useCallback } from 'react';

interface RedisMockStockData {
  stock_code: string;
  stock_name: string;
  change_rate: number;
  volume: number;
  price: number;
  timestamp: number;
}

interface UseBTCoinBacktestReturn {
  data: RedisMockStockData[] | null;
  loading: boolean;
  error: string | null;
  refetch: (coinCode?: string, startTime?: string, endTime?: string) => Promise<any>;
}

export const useBTCoinBacktest = (): UseBTCoinBacktestReturn => {
  const [data, setData] = useState<RedisMockStockData[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (coinCode?: string, startTime?: string, endTime?: string): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      // 모든 Redis 키에서 데이터 조회 (coin_code 파라미터 추가)
      // UTC 2025-09-01 데이터는 제외
      let url = `/mercury-backend/bt/coin-backtest?exclude_date=2025-09-01`;
      if (coinCode) {
        url += `&coin_code=${encodeURIComponent(coinCode)}`;
      }
      // 시간 범위 필터링 추가
      if (startTime && endTime) {
        // 시간대 정보가 없으면 'Z'를 붙여서 UTC로 전달
        let startTimeParam = startTime;
        let endTimeParam = endTime;
        // T가 있지만 Z나 +, - (타임존)이 없으면 Z를 붙임
        if (startTimeParam.includes('T') && !startTimeParam.includes('Z') && !startTimeParam.match(/[+-]\d{2}:\d{2}$/)) {
          startTimeParam = startTimeParam + 'Z';
        } else if (!startTimeParam.includes('T') && !startTimeParam.includes('Z') && !startTimeParam.match(/[+-]\d{2}:\d{2}$/)) {
          startTimeParam = startTimeParam + 'Z';
        }
        if (endTimeParam.includes('T') && !endTimeParam.includes('Z') && !endTimeParam.match(/[+-]\d{2}:\d{2}$/)) {
          endTimeParam = endTimeParam + 'Z';
        } else if (!endTimeParam.includes('T') && !endTimeParam.includes('Z') && !endTimeParam.match(/[+-]\d{2}:\d{2}$/)) {
          endTimeParam = endTimeParam + 'Z';
        }
        url += `&start_time=${encodeURIComponent(startTimeParam)}&end_time=${encodeURIComponent(endTimeParam)}`;
        console.log('🔍 [코인 백테스트] 시간 범위 필터링:', { startTime: startTimeParam, endTime: endTimeParam, url });
      }
      
      const response = await fetch(url);
      
      const result = await response.json();
      
      if (!response.ok) {
        // 백엔드에서 JSON 에러 응답을 반환하는 경우
        const errorMessage = result.error || result.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }
      const dataArray = result.data || [];
      setData(dataArray);
      
      // 키 파싱 에러 확인 및 표시
      if (result.extra && result.extra.parseErrors && result.extra.parseErrors.length > 0) {
        const errorCount = result.extra.errorCount || result.extra.parseErrors.length;
        const errorMessages = result.extra.parseErrors.slice(0, 10); // 최대 10개만 표시
        const errorMessage = `키 파싱 에러 ${errorCount}개 발생:\n${errorMessages.join('\n')}${errorCount > 10 ? `\n... 외 ${errorCount - 10}개 에러` : ''}`;
        setError(errorMessage);
        console.error('❌ 키 파싱 에러:', result.extra.parseErrors);
      } else if (!result.success && result.message) {
        // success가 false이고 메시지가 있는 경우
        setError(result.message);
      } else if (result.extra && result.extra.errorCount > 0) {
        // errorCount가 있는 경우
        setError(`키 파싱 에러 ${result.extra.errorCount}개 발생`);
      } else {
        // 에러가 없으면 기존 에러 초기화
        setError(null);
      }
      
      // 시간 범위 확인
      if (dataArray.length > 0) {
        const timestamps = dataArray
          .map((item: any) => {
            const ts = item.timestamp || item.Timestamp;
            if (typeof ts === 'number') {
              return ts;
            } else if (typeof ts === 'string') {
              return new Date(ts).getTime();
            }
            return null;
          })
          .filter((ts: number | null): ts is number => ts !== null)
          .sort((a: number, b: number) => a - b);
        
        const minTime = timestamps[0] ? new Date(timestamps[0]).toISOString() : 'N/A';
        const maxTime = timestamps[timestamps.length - 1] ? new Date(timestamps[timestamps.length - 1]).toISOString() : 'N/A';
        
        console.log('📊 코인 백테스트 데이터 조회 완료:', {
          총데이터개수: dataArray.length,
          시간범위: {
            최소시간: minTime,
            최대시간: maxTime,
            시간차이: timestamps.length > 0 ? `${((timestamps[timestamps.length - 1]! - timestamps[0]!) / 1000 / 60).toFixed(2)}분` : 'N/A'
          },
          백엔드응답: {
            success: result.success,
            count: result.count,
            extra: result.extra
          }
        });
      } else {
        console.log('📊 코인 백테스트 데이터 조회 완료: 데이터 없음');
      }
      
      // 응답 반환 (백엔드 응답 전체)
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching coin backtest data:', err);
      return null; // 에러 시 null 반환
    } finally {
      setLoading(false);
    }
    }, []);

  // 페이지 로드 시 자동 호출 (coinCode 없이)
  // useEffect(() => {
  //   fetchData();
  // }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};

